/**
 * @param incomingValue - a number from an <input> or a slider thumb
 * @param steps - a discrete list of steps that the slider is allowed to use as values
 * @param roundType - the rounding type to use when a user enters a number that is not one of the
 * listed `steps`. 'up' rounds to the next step up, 'down' rounds to the next step down, and
 * 'nearest' rounds to the nearest step value.
 */
export const getStepValue = (
    incomingValue: number,
    steps: number[],
    roundType: SliderStepRoundType,
): number => {
    // incomingValue is determined by the minStepSize and the position of the slider thumb. It may
    // not actually be a value in the `steps` list; our task here is to determine which value from
    // the `steps` list to return.
    const nextStepIndex =
        steps.findIndex(
            (currentStepValue: number): boolean =>
                currentStepValue >= incomingValue,
        ) ?? -1;

    // nextStepValue is the next highest step above incomingValue
    const nextStepValue = steps[nextStepIndex];

    // If there is no nextStepValue, it means the incomingValue is greater than any of the step
    // values, so we should return the highest step value
    if (nextStepValue === undefined) {
        return steps[steps.length - 1];
    }

    // If the incoming value matches a step exactly, just return it as is
    if (nextStepValue === incomingValue) {
        return incomingValue;
    }

    const prevStepValue = steps[nextStepIndex - 1];

    // If there is no prevStepValue, it means the incomingValue is less than any of the step values,
    // so we should return the lowest step value
    if (prevStepValue === undefined) {
        return steps[0];
    }

    // If roundType is 'nearest', use match to find the nearest step value and return that
    if (roundType === 'nearest') {
        const distanceToNextStepValue = Math.abs(nextStepValue - incomingValue);
        const distanceToPrevStepValue = Math.abs(prevStepValue - incomingValue);

        if (distanceToNextStepValue <= distanceToPrevStepValue) {
            return nextStepValue;
        }

        return prevStepValue;
    }

    if (roundType === 'down') {
        return prevStepValue;
    }

    // If we reached this point, `roundType` is 'up' and we should return nextStepValue
    return nextStepValue;
};
